import { SmartRagApiCall } from '../../../Utils/Api/Api';

/**
 * Creates a new chat session.
 * @async
 * @param {string} tenantId - The Tenant ID.
 * @param {Object} chatDetails - The details for creating a chat session.
 * @returns {Promise<Object>} The response object.
 * @throws {Error} If an error occurs while creating the chat session.
 */
const sendQuestion = async (tenantId, chatDetails) => {
  try {
    const headers = {
      'Tenant-ID': tenantId,
      'Content-Type': 'application/json',
    };
    const response = await SmartRagApiCall(
      `/chat`,
      'POST',
      chatDetails,
      headers,
    );

    return {
      ...response,
      message:
        response.message ||
        (response.isSuccess
          ? 'Chat session created successfully!'
          : 'An error occurred while creating the chat session.'),
    };
  } catch (error) {
    throw new Error(`Error creating chat session: ${error.message}`);
  }
};

/**
 * Streams a response to a given question in a chat session.
 * @async
 * @param {string} tenantId - The Tenant ID.
 * @param {Object} data - The data to send for streaming the response.
 * @param {Function} setMessages - Function to update chat messages.
 * @returns {Promise<void>} Streams the response.
 * @throws {Error} If an error occurs while streaming the response.
 */
const handleSmartRagStream = async (tenantId, data, setMessages) => {
  const url = '/chat';
  const headers = {
    'Tenant-ID': tenantId,
    'Content-Type': 'application/json',
  };

  try {
    const response = await SmartRagApiCall(url, 'POST', data, headers, true);

    if (!response.isSuccess) {
      throw new Error(response.message);
    }

    const reader = response.data.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';
    let combinedMessage = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // Process complete JSON objects from the buffer
      let start = buffer.indexOf('{');
      let end = buffer.indexOf('}', start);

      while (start !== -1 && end !== -1 && end > start) {
        let jsonString = buffer.substring(start, end + 1);

        try {
          // First try parsing as-is (might be valid JSON with escaped quotes)
          let parsedMessage;
          try {
            parsedMessage = JSON.parse(jsonString);
          } catch (e) {
            // If that fails, try with proper JSON escaping
            // Replace only single quotes that aren't part of a contraction
            const fixedJsonString = jsonString
              .replace(/(\w)'(\w)/g, "$1\\'$2") // Escape apostrophes in words
              .replace(/'/g, '"') // Replace remaining single quotes with double quotes
              .replace(/\\"/g, "'"); // Convert escaped double quotes back to single quotes

            parsedMessage = JSON.parse(fixedJsonString);
          }

          // Check if the JSON contains "citations" and skip if necessary
          if (parsedMessage.citations) {
            buffer = buffer.substring(end + 1);
            start = buffer.indexOf('{');
            end = buffer.indexOf('}', start);
            continue;
          }

          if (parsedMessage.answer) {
            combinedMessage += parsedMessage.answer;

            setMessages((prevMessages) => {
              const lastMessageIndex = prevMessages.length - 1;
              if (prevMessages[lastMessageIndex]?.role === 'assistant') {
                const updatedMessages = [...prevMessages];
                updatedMessages[lastMessageIndex].text = combinedMessage;
                return updatedMessages;
              } else {
                return [
                  ...prevMessages,
                  {
                    role: 'assistant',
                    text: combinedMessage,
                    timestamp: new Date(),
                  },
                ];
              }
            });
          }
        } catch (error) {
          console.error('Error parsing JSON chunk:', error, jsonString);
          // Skip this chunk if we can't parse it
        }

        // Update buffer to process remaining data
        buffer = buffer.substring(end + 1);
        start = buffer.indexOf('{');
        end = buffer.indexOf('}', start);
      }
    }
  } catch (error) {
    throw new Error(`Error streaming response: ${error.message}`);
  }
};

export { sendQuestion, handleSmartRagStream };
